import React from 'react';
import { observer } from 'mobx-react-lite';
import { formatAmount, formatValue } from '../../utils/formatters';
import styles from './CalculationPreview.module.css';

/**
 * Компонент для рендеринга предпросмотра расчета/шаблона
 * Использует гибридно-модельный подход на основе структуры UniversalEditor
 */
const CalculationPreviewRenderer = observer(({ data: initialData, isTemplate = false }) => {
    // Используем переданную MST модель напрямую
    // initialData уже является MST моделью из локальной коллекции проекта с вычисляемыми полями
    const data = initialData;

    if (!data) return null;

    // Определяем тип расчета
    const calculatorType = data.type || data.calculatorType || 'works';
    const hourRate = data.hourRate || data.settings?.hourRate || 0;
    const includeVAT = data.includeVAT ?? data.settings?.includeVAT ?? false;
    const discount = isTemplate ? 0 : (data.discount || 0);
    const content = data.content || {};

    // Рендеринг блока настроек (Header)
    const renderHeader = () => {
        return (
            <div className={styles.previewBlock}>
                <h3 className={styles.blockTitle}>Настройки расчета</h3>
                <div className={styles.settingsGrid}>
                    {/* Группа: Ставка и НДС */}
                    <div className={styles.settingsGroup}>
                        <div className={styles.settingItem}>
                            <span className={styles.settingLabel}>Ставка (без НДС):</span>
                            <span className={styles.settingValue}>{formatAmount(hourRate)}/час</span>
                        </div>
                        <div className={styles.settingItem}>
                            <span className={styles.settingLabel}>НДС:</span>
                            <span className={styles.settingValue}>{includeVAT ? 'Включен (20%)' : 'Не включен'}</span>
                        </div>
                    </div>

                    {/* Скидка (если есть) */}
                    {!isTemplate && discount > 0 && (
                        <div className={styles.settingsGroup}>
                            <div className={styles.settingItem}>
                                <span className={styles.settingLabel}>Скидка:</span>
                                <span className={styles.settingValue}>{discount}%</span>
                            </div>
                        </div>
                    )}

                    {/* Группа: Длительность и Специалисты */}
                    {((data.duration > 0 || data.estimatedDuration > 0) || data.specialistsCount > 0) && (
                        <div className={styles.settingsGroup}>
                            {(data.duration > 0 || data.estimatedDuration > 0) && (
                                <div className={styles.settingItem}>
                                    <span className={styles.settingLabel}>Длительность:</span>
                                    <span className={styles.settingValue}>{data.duration || data.estimatedDuration} дн.</span>
                                </div>
                            )}
                            {data.specialistsCount > 0 && (
                                <div className={styles.settingItem}>
                                    <span className={styles.settingLabel}>Специалистов:</span>
                                    <span className={styles.settingValue}>{data.specialistsCount} чел.</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Рендеринг тела расчета на основе типа (Body)
    const renderBody = () => {
        switch (calculatorType) {
            case 'works':
                return renderWorksBody();
            case 'documents':
                return renderDocumentsBody();
            case 'support':
                return renderSupportBody();
            default:
                return <div>Неизвестный тип расчета</div>;
        }
    };

    // Рендеринг тела для типа "works" с итогами
    const renderWorksBody = () => {
        const stages = content.stages || [];
        const hasDiscount = discount > 0 && !isTemplate;

        if (stages.length === 0) {
            return (
                <div className={styles.previewBlock}>
                    <h3 className={styles.blockTitle}>Этапы работ</h3>
                    <p className={styles.emptyMessage}>Нет этапов работ</p>
                </div>
            );
        }

        return (
            <div className={styles.previewBlock}>
                <h3 className={styles.blockTitle}>Этапы работ</h3>
                <table className={styles.worksTable}>
                    <thead>
                        <tr>
                            <th className={styles.thNumber}>№</th>
                            <th className={styles.thName}>Наименование</th>
                            <th className={styles.thDescription}>Описание</th>
                            <th className={styles.thHours}>Часы</th>
                            <th className={styles.thCost}>Стоимость<br/>(без НДС)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stages.map((stage, stageIndex) => {
                            const stageNum = stageIndex + 1;
                            const stageWorks = stage.works || [];

                            return (
                                <React.Fragment key={stage.id || stageIndex}>
                                    {/* Строка этапа */}
                                    <tr className={styles.stageRow}>
                                        <td className={styles.tdNumber}>{stageNum}</td>
                                        <td className={styles.tdName} colSpan="4">
                                            <strong>{stage.name || `Этап ${stageNum}`}</strong>
                                        </td>
                                    </tr>

                                    {/* Строки работ этапа */}
                                    {stageWorks.map((work, workIndex) => {
                                        const workNum = `${stageNum}.${workIndex + 1}`;

                                        return (
                                            <tr key={work.id || `${stageIndex}-${workIndex}`} className={styles.workRow}>
                                                <td className={styles.tdNumber}>{workNum}</td>
                                                <td className={styles.tdName}>{work.name || ''}</td>
                                                <td className={styles.tdDescription}>{work.description || ''}</td>
                                                <td className={styles.tdHours}>{work.totalHours || 0}</td>
                                                <td className={styles.tdCost}>{formatAmount(work.cost || 0)}</td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}

                        {/* Разделитель перед итогами */}
                        <tr className={styles.summaryDivider}>
                            <td colSpan="5"></td>
                        </tr>

                        {/* Строки итогов */}
                        <tr className={styles.summaryMainRow}>
                            <td className={styles.tdNumber} colSpan="3" style={{textAlign: 'left'}}>
                                <strong>ИТОГО</strong>
                            </td>
                            <td className={styles.tdHours}>
                                <strong>{data.totalHours || 0}</strong>
                            </td>
                            <td className={styles.tdCost}>
                                <strong>{formatAmount(data.subtotal || data.costAfterDiscount || 0)}</strong>
                            </td>
                        </tr>

                        {/* Строка со скидкой */}
                        {hasDiscount && (
                            <tr>
                                <td className={styles.tdNumber} colSpan="3" style={{textAlign: 'left'}}>
                                    Стоимость с учетом скидки ({discount}%)
                                </td>
                                <td className={styles.tdHours}>-</td>
                                <td className={styles.tdCost}>
                                    {formatAmount(data.costAfterDiscount || 0)}
                                </td>
                            </tr>
                        )}

                        {/* Финальная строка с НДС */}
                        {includeVAT && (
                            <tr className={styles.summaryFinalRow}>
                                <td className={styles.tdNumber} colSpan="3" style={{textAlign: 'left'}}>
                                    <strong>
                                        {hasDiscount
                                            ? 'ИТОГО с учетом скидки и НДС 20%'
                                            : 'ИТОГО с НДС 20%'}
                                    </strong>
                                </td>
                                <td className={styles.tdHours}>-</td>
                                <td className={styles.tdCost}>
                                    <strong>{formatAmount(data.total || 0)}</strong>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    // Рендеринг тела для типа "documents" с итогами
    const renderDocumentsBody = () => {
        const documents = content.documents || [];
        const hasDiscount = discount > 0 && !isTemplate;

        if (documents.length === 0) {
            return (
                <div className={styles.previewBlock}>
                    <h3 className={styles.blockTitle}>Документация</h3>
                    <p className={styles.emptyMessage}>Нет документов</p>
                </div>
            );
        }

        return (
            <div className={styles.previewBlock}>
                <h3 className={styles.blockTitle}>Документация</h3>
                <table className={styles.documentsTable}>
                    <thead>
                        <tr>
                            <th className={styles.thNumber}>№</th>
                            <th className={styles.thName}>Наименование документа</th>
                            <th className={styles.thQuantity}>Количество</th>
                            <th className={styles.thHoursPerUnit}>Часы на ед.</th>
                            <th className={styles.thHours}>Всего часов</th>
                            <th className={styles.thCost}>Стоимость<br/>(без НДС)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {documents.map((doc, index) => {
                            return (
                                <tr key={doc.id || index}>
                                    <td className={styles.tdNumber}>{index + 1}</td>
                                    <td className={styles.tdName}>{doc.name || ''}</td>
                                    <td className={styles.tdQuantity}>{doc.quantity || 1}</td>
                                    <td className={styles.tdHoursPerUnit}>{doc.hoursPerUnit || 0}</td>
                                    <td className={styles.tdHours}>{doc.totalHours || 0}</td>
                                    <td className={styles.tdCost}>{formatAmount(doc.sum || 0)}</td>
                                </tr>
                            );
                        })}

                        {/* Разделитель перед итогами */}
                        <tr className={styles.summaryDivider}>
                            <td colSpan="6"></td>
                        </tr>

                        {/* Строки итогов */}
                        <tr className={styles.summaryMainRow}>
                            <td className={styles.tdNumber} colSpan="4" style={{textAlign: 'left'}}>
                                <strong>ИТОГО</strong>
                            </td>
                            <td className={styles.tdHours}>
                                <strong>{data.totalHours || 0}</strong>
                            </td>
                            <td className={styles.tdCost}>
                                <strong>{formatAmount(data.subtotal || data.costAfterDiscount || 0)}</strong>
                            </td>
                        </tr>

                        {/* Строка со скидкой */}
                        {hasDiscount && (
                            <tr>
                                <td className={styles.tdNumber} colSpan="4" style={{textAlign: 'left'}}>
                                    Стоимость с учетом скидки ({discount}%)
                                </td>
                                <td className={styles.tdHours}>-</td>
                                <td className={styles.tdCost}>
                                    {formatAmount(data.costAfterDiscount || 0)}
                                </td>
                            </tr>
                        )}

                        {/* Финальная строка с НДС */}
                        {includeVAT && (
                            <tr className={styles.summaryFinalRow}>
                                <td className={styles.tdNumber} colSpan="4" style={{textAlign: 'left'}}>
                                    <strong>
                                        {hasDiscount
                                            ? 'ИТОГО с учетом скидки и НДС 20%'
                                            : 'ИТОГО с НДС 20%'}
                                    </strong>
                                </td>
                                <td className={styles.tdHours}>-</td>
                                <td className={styles.tdCost}>
                                    <strong>{formatAmount(data.total || 0)}</strong>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    // Рендеринг тела для типа "support"
    const renderSupportBody = () => {
        const supportDetails = content.supportDetails || {};
        const months = supportDetails.months || 0;
        const hoursPerMonth = supportDetails.hoursPerMonth || 0;
        const totalHours = months * hoursPerMonth;

        // Берем готовые данные из объекта data
        const hasDiscount = discount > 0 && !isTemplate;

        return (
            <div className={styles.previewBlock}>
                <h3 className={styles.blockTitle}>Техническая поддержка</h3>
                <table className={styles.worksTable}>
                    <thead>
                        <tr>
                            <th className={styles.thName}>Параметр</th>
                            <th className={styles.thDescription}>Значение</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className={styles.tdName}>Период</td>
                            <td className={styles.tdDescription} style={{textAlign: 'right'}}>{months} месяцев</td>
                        </tr>
                        <tr>
                            <td className={styles.tdName}>Часов в месяц</td>
                            <td className={styles.tdDescription} style={{textAlign: 'right'}}>{hoursPerMonth}</td>
                        </tr>
                        <tr>
                            <td className={styles.tdName}>Всего часов</td>
                            <td className={styles.tdDescription} style={{textAlign: 'right'}}>{months} × {hoursPerMonth} = {totalHours}</td>
                        </tr>
                        {supportDetails.description && (
                            <tr>
                                <td className={styles.tdName}>Описание</td>
                                <td className={styles.tdDescription} style={{textAlign: 'right'}}>
                                    {supportDetails.description}
                                </td>
                            </tr>
                        )}

                        {/* Разделитель перед итогами */}
                        <tr className={styles.summaryDivider}>
                            <td colSpan="2"></td>
                        </tr>

                        {/* Строка ИТОГО */}
                        <tr className={styles.summaryMainRow}>
                            <td className={styles.tdName}>
                                <strong>ИТОГО</strong>
                            </td>
                            <td className={styles.tdDescription} style={{textAlign: 'right'}}>
                                <strong>{formatAmount(data.subtotal || data.costAfterDiscount || 0)}</strong>
                            </td>
                        </tr>

                        {/* Строка со скидкой (если есть) */}
                        {hasDiscount && (
                            <tr>
                                <td className={styles.tdName}>
                                    Стоимость с учетом скидки ({discount}%)
                                </td>
                                <td className={styles.tdDescription} style={{textAlign: 'right'}}>
                                    {formatAmount(data.costAfterDiscount || 0)}
                                </td>
                            </tr>
                        )}

                        {/* Финальная строка с НДС (если включен) */}
                        {includeVAT && (
                            <tr className={styles.summaryFinalRow}>
                                <td className={styles.tdName}>
                                    <strong>
                                        {hasDiscount
                                            ? 'ИТОГО с учетом скидки и НДС 20%'
                                            : 'ИТОГО с НДС 20%'}
                                    </strong>
                                </td>
                                <td className={styles.tdDescription} style={{textAlign: 'right'}}>
                                    <strong>{formatAmount(data.total || 0)}</strong>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    // Рендеринг примечаний (Notes)
    const renderNotes = () => {
        // Поддерживаем обе структуры: старую (data.notes) и новую (data.notesData.notes)
        const notes = data.notesData?.notes || data.notes || [];

        // Фильтруем пустые примечания
        const nonEmptyNotes = notes.filter(note => {
            const noteText = typeof note === 'string' ? note : note.text;
            return noteText && noteText.trim();
        });

        if (nonEmptyNotes.length === 0) return null;

        return (
            <div className={styles.previewBlock}>
                <h3 className={styles.blockTitle}>Примечания:</h3>
                <ol className={styles.notesList}>
                    {nonEmptyNotes.map((note, index) => {
                        const noteText = typeof note === 'string' ? note : note.text;
                        return (
                            <li key={note.id || index} className={styles.noteItem}>
                                {noteText}
                            </li>
                        );
                    })}
                </ol>
            </div>
        );
    };



    return (
        <div className={styles.previewContainer}>
            {renderHeader()}
            {renderBody()}
            {renderNotes()}
        </div>
    );
});

export default CalculationPreviewRenderer;